import React, { useRef, useState, useEffect } from "react";
import { motion, useScroll, useTransform, useSpring } from "framer-motion";
import { Star, Quote } from "lucide-react";
import SectionBadge from "../../../components/ui/SectionBadge";
import { getApprovedTestimonials } from "../../../api/testimonials";

const TestimonialsSection = () => {
    const sectionRef = useRef(null);
    const [testimonials, setTestimonials] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    const { scrollYProgress } = useScroll({
        target: sectionRef,
        offset: ["start end", "end start"]
    });

    const springConfig = { stiffness: 100, damping: 30, restDelta: 0.001 };
    const y = useSpring(useTransform(scrollYProgress, [0, 1], [50, -50]), springConfig);

    // Fetch testimonials on component mount
    useEffect(() => {
        const loadTestimonials = async () => {
            try {
                const data = await getApprovedTestimonials();
                setTestimonials(data);
            } catch (error) {
                console.error('Error loading testimonials:', error);
            } finally {
                setIsLoading(false);
            }
        };

        loadTestimonials();
    }, []);

    // Duplicate testimonials for seamless loop
    const duplicatedTestimonials = [...testimonials, ...testimonials];

    return (
        <section ref={sectionRef} className="relative py-10  overflow-hidden bg-white border-t border-gray-200">
            {/* Background grid */}
            <motion.div
                style={{ y, opacity: 0.05 }}
                className="absolute inset-0"
            >
                <svg className="w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
                    <defs>
                        <pattern id="testimonials-grid" width="8" height="8" patternUnits="userSpaceOnUse">
                            <path d="M 8 0 L 0 0 0 8" fill="none" stroke="currentColor" strokeWidth="0.5" className="text-gray-300" />
                        </pattern>
                    </defs>
                    <rect width="100" height="100" fill="url(#testimonials-grid)" />
                </svg>
            </motion.div>

            {/* Floating orbs */}
            <motion.div
                style={{ y: useTransform(scrollYProgress, [0, 1], [0, 100]) }}
                className="absolute top-20 right-10 w-72 h-72 bg-gradient-to-br from-blue-400 to-purple-400 rounded-full mix-blend-multiply filter blur-3xl opacity-15"
                animate={{ scale: [1, 1.05, 1] }}
                transition={{ duration: 18, repeat: Infinity, ease: "easeInOut" }}
            />
            <motion.div
                style={{ y: useTransform(scrollYProgress, [0, 1], [0, -100]) }}
                className="absolute bottom-20 left-10 w-80 h-80 bg-gradient-to-br from-pink-400 to-orange-400 rounded-full mix-blend-multiply filter blur-3xl opacity-15"
                animate={{ scale: [1, 1.05, 1] }}
                transition={{ duration: 20, repeat: Infinity, ease: "easeInOut" }}
            />

            <div className="container mx-auto relative z-10 3xl:max-w-[1800px]">
                {/* Section Header */}
                <motion.div
                    className="text-center max-w-3xl 3xl:max-w-4xl 4xl:max-w-5xl mx-auto mb-16 3xl:mb-24"
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6 }}
                >
                    <motion.div
                        className="inline-flex items-center gap-2 px-4 py-2 mt-10 rounded-full bg-gradient-to-r from-blue-500 to-purple-500 text-white mb-6 shadow-lg 3xl:px-6 3xl:py-3"
                        initial={{ opacity: 0, scale: 0.9 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.4 }}
                    >
                        <motion.div
                            animate={{ scale: [1, 1.2, 1] }}
                            transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                        >
                            <Star className="w-4 h-4 3xl:w-5 3xl:h-5 text-yellow-300 fill-yellow-300" />
                        </motion.div>
                        <span className="text-sm 3xl:text-base 4xl:text-lg 5xl:text-xl 6xl:text-2xl font-semibold">Testimonials</span>
                    </motion.div>

                    <h2 className="text-4xl md:text-5xl 3xl:text-6xl font-bold mb-6 3xl:mb-8 bg-gradient-to-r from-slate-900 via-blue-800 to-purple-900 bg-clip-text text-transparent leading-normal py-1">
                        What Our Clients Say About Us
                    </h2>
                </motion.div>
                {/* Auto-scrolling Testimonials */}
                <div className="relative py-10 3xl:py-16">
                    <div className="overflow-hidden">
                        <motion.div
                            className="flex gap-6 3xl:gap-10"
                            animate={{
                                x: [0, -2500], // Increased travel distance for wider cards/gaps
                            }}
                            transition={{
                                x: {
                                    duration: 40,
                                    repeat: Infinity,
                                    ease: "linear",
                                },
                            }}
                        >
                            {duplicatedTestimonials.map((testimonial, index) => (
                                <motion.div
                                    key={`${testimonial.id}-${index}`}
                                    className="group flex-shrink-0 w-[420px] 3xl:w-[500px] 4xl:w-[600px] bg-white rounded-2xl p-8 3xl:p-10 shadow-sm hover:shadow-xl transition-all duration-300 border border-gray-100"
                                    whileHover="hover"
                                >
                                    {/* Quote Icon */}
                                    <div className="mb-6">
                                        <Quote className="w-10 h-10 3xl:w-12 3xl:h-12 text-blue-500 opacity-50" />
                                    </div>

                                    {/* Testimonial Text */}
                                    <p className="text-gray-700 text-base 3xl:text-xl 4xl:text-2xl 5xl:text-3xl 6xl:text-4xl leading-relaxed mb-8 min-h-[100px] 3xl:min-h-[120px]">
                                        "{testimonial.quote}"
                                    </p>

                                    {/* Rating Stars */}
                                    <div className="flex gap-1 mb-6">
                                        {[...Array(testimonial.rating)].map((_, i) => (
                                            <Star key={i} className="w-4 h-4 3xl:w-5 3xl:h-5 text-yellow-400 fill-yellow-400" />
                                        ))}
                                    </div>

                                    {/* Author Info */}
                                    <div className="flex items-center gap-4 pt-6 border-t border-gray-100">
                                        <img
                                            src={testimonial.avatar}
                                            alt={testimonial.author}
                                            className="w-12 h-12 3xl:w-16 3xl:h-16 rounded-full object-cover ring-2 ring-blue-100"
                                        />
                                        <div>
                                            <h4 className="font-bold text-gray-900 text-sm 3xl:text-lg 4xl:text-xl 5xl:text-2xl 6xl:text-3xl">{testimonial.author}</h4>
                                            <p className="text-xs 3xl:text-sm 4xl:text-base 5xl:text-lg 6xl:text-xl text-gray-500">{testimonial.role}, {testimonial.company}</p>
                                        </div>
                                    </div>
                                </motion.div>
                            ))}
                        </motion.div>
                    </div>

                    {/* Gradient Overlays for fade effect */}
                    <div className="absolute left-0 top-0 bottom-0 w-32 3xl:w-48 bg-gradient-to-r from-white to-transparent pointer-events-none" />
                    <div className="absolute right-0 top-0 bottom-0 w-32 3xl:w-48 bg-gradient-to-l from-white to-transparent pointer-events-none" />
                </div>
            </div>
        </section>
    );
};

export default TestimonialsSection;